import { MutableRefObject, useEffect, useRef, useState } from 'react'
import { useBlocker } from 'react-router-dom'
import { FilterTab } from './filterTabs'

const normalizeSql = (sql?: string | null) => (sql ?? '').trim()

// Tracks whether the filter the chat applied still needs the "save as reusable feature" decision, as the SQL tab does.
// A query already saved on the experiment, or saved here as a feature, is not offered again.
export const useChatQuerySave = (active: boolean, appliedSql?: string | null, experimentSavedSql?: string | null) => {
    const modalRef = useRef<any>(null)
    const [savedQueries, setSavedQueries] = useState<string[]>([])
    const applied = normalizeSql(appliedSql)
    const unsaved = active && !!applied && applied !== normalizeSql(experimentSavedSql) && !savedQueries.includes(applied)

    return {
        modalRef,
        unsaved,
        open: () => modalRef.current?.showModal(),
        markSaved: (query: string) => setSavedQueries((prev) => [...prev, normalizeSql(query)]),
    }
}

export type ChatQuerySave = ReturnType<typeof useChatQuerySave>

interface SavedFilterSql {
    sql_query?: string | null
    sql?: string | null
}

interface UseQuerySaveStateOptions {
    filterTab: FilterTab
    filterKey: string
    experiment: any
    chatSavedFilter?: SavedFilterSql | null
    sqls?: { sql_query?: string | null } | null
    currentQueryExecuted: boolean
    sqlRef: MutableRefObject<any>
    experimentLiteral?: string
}

// Shared by both chat-filter hosts (CreateExperiment and Measurement): resolves the saved query to
// compare against, the chat's "save as reusable feature" state, whether a save decision is still
// pending, and the route blocker that stops navigation away from an unsaved query.
export const useQuerySaveState = ({ filterTab, filterKey, experiment, chatSavedFilter, sqls, currentQueryExecuted, sqlRef, experimentLiteral }: UseQuerySaveStateOptions) => {
    const currentSavedFilter = chatSavedFilter ?? experiment?.experiment_metadatas?.[filterKey]
    const currentSavedSql = currentSavedFilter?.sql_query ?? currentSavedFilter?.sql
    const chatSave = useChatQuerySave(filterTab === 'nl2sql', sqls?.sql_query, currentSavedSql)
    // The query waiting for a save-as-feature decision: the chat's applied filter on that tab, the executed SQL otherwise.
    const querySavePending = filterTab === 'nl2sql' ? chatSave.unsaved : currentQueryExecuted
    const saveQuery = () => (filterTab === 'nl2sql' ? chatSave.open() : sqlRef?.current?.saveFeatures())
    const blocker = useBlocker(!experimentLiteral && querySavePending)
    return { currentSavedFilter, currentSavedSql, chatSave, querySavePending, saveQuery, blocker }
}

export const useQuerySaveBlocker = (blocker: { state: string; proceed?: () => void }, onBlocked: () => void, skipNextPromptRef: MutableRefObject<boolean>) => {
    const onBlockedRef = useRef(onBlocked)
    onBlockedRef.current = onBlocked

    useEffect(() => {
        if (blocker.state !== 'blocked') return
        if (skipNextPromptRef.current) {
            skipNextPromptRef.current = false
            blocker.proceed?.()
            return
        }
        onBlockedRef.current()
    }, [blocker, skipNextPromptRef])
}
